import type {
  BooleanControl,
  CheckboxControl,
  DateControl,
  DateRangeControl,
  FilterControl,
  MultiSelectControl,
  NumberRangeControl,
  SingleSelectControl,
  TextControl,
  TimeRangeControl
} from '../../../types/filterControl';
import { type DateRangeDefaults, dateRangeSpec, dateSpec, numberRangeSpec, timeRangeSpec } from './filterRangeSpecs';
import { multiSelectSpec, singleSelectSpec } from './filterSelectSpecs';
import {
  booleanSpec,
  type ControlText,
  type FilterOptionsInput,
  type FilterSpec,
  type QuickFilterSpec,
  textSpec
} from './filterSpecs';

// Binds each builder to the endpoint's query at type level; the specs in `filterSpecs` do the runtime work.

type ParamKey<Q> = Extract<keyof Q, string>;
type ExcludedKey<K extends string> = `excluded${Capitalize<K>}`;
/**
 * An exclusion is `excluded<Key>` (or the older `exclude<Key>`, `excludeStatusIds`) of a `key` the endpoint also
 * takes: never a control's own param. A flag that merely starts with the word (`excludedCustomer`, no `customer`)
 * is an ordinary param.
 */
type IsExclusion<Q, K extends string> = K extends `excluded${infer R}`
  ? Uncapitalize<R> extends keyof Q
    ? true
    : false
  : K extends `exclude${infer R}`
    ? Uncapitalize<R> extends keyof Q
      ? true
      : false
    : false;
type ElementOf<Q, K extends keyof Q> = NonNullable<Q[K]> extends readonly (infer E)[] ? E : never;
type OptionValue<Q, K extends keyof Q> = Extract<ElementOf<Q, K>, string | number>;
type ScalarValue<Q, K extends keyof Q> = Extract<NonNullable<Q[K]>, string | number>;

/** Include params whose values are arrays of strings or numbers. */
type ArrayKeyOf<Q> = {
  [K in ParamKey<Q>]-?: IsExclusion<Q, K> extends true
    ? never
    : NonNullable<Q[K]> extends readonly (infer E)[]
      ? [E] extends [string | number]
        ? K
        : never
      : never;
}[ParamKey<Q>];

/**
 * Include params a free input can write: they take every `T` (`string`, not an enum), so typing or picking any
 * value never sends one the endpoint rejects.
 */
type FreeKeyOf<Q, T> = {
  [K in ParamKey<Q>]-?: IsExclusion<Q, K> extends true
    ? never
    : NonNullable<Q[K]> extends readonly unknown[]
      ? never
      : [T] extends [NonNullable<Q[K]>]
        ? K
        : never;
}[ParamKey<Q>];

/** Include params holding one value of type `T` (an enum is fine: a select only offers its options). */
type ValueKeyOf<Q, T> = {
  [K in ParamKey<Q>]-?: IsExclusion<Q, K> extends true
    ? never
    : NonNullable<Q[K]> extends readonly unknown[]
      ? never
      : [NonNullable<Q[K]>] extends [T]
        ? K
        : never;
}[ParamKey<Q>];

/** `createdAt` when the endpoint takes `createdAtGte` + `createdAtLte`, both taking every `T` (free inputs). */
type RangeBaseOf<Q, T> = {
  [K in ParamKey<Q>]-?: IsExclusion<Q, K> extends true
    ? never
    : K extends `${infer B}Gte`
      ? `${B}Lte` extends keyof Q
        ? [T, T] extends [NonNullable<Q[K]>, NonNullable<Q[`${B}Lte` & keyof Q]>]
          ? B
          : never
        : never
      : never;
}[ParamKey<Q>];

/** Time-of-day ranges (`createdAtTimeGte`, `HH:MM`) are strings too; a date range must not take them. */
type DateRangeBaseOf<Q> = Exclude<RangeBaseOf<Q, string>, `${string}Time`>;
type TimeRangeBaseOf<Q> = Extract<RangeBaseOf<Q, string>, `${string}Time`>;

type RangeKeys<B extends string> = `${B}Gte` | `${B}Lte`;

/** An excluded param whose type cannot hold the include param's values: the builder refuses it. */
type ExcludeTypeMismatch = { error: 'the excluded param cannot take the values of the include param' };

/** Whether `excluded<K>` exists and takes `TValues` (what the control would exclude). */
type ExcludeFits<Q, K extends string, TValues> =
  ExcludedKey<K> extends keyof Q
    ? [TValues] extends [NonNullable<Q[ExcludedKey<K>]>]
      ? 'fits'
      : 'mismatch'
    : 'absent';

/**
 * Exclude is decided per control at the call site. Endpoint takes `excluded<Key>` of a type that holds the
 * values: `exclude` is required -- `true`, or `false` to keep it off on purpose (invoicing must not skip a hidden
 * department) -- so no control drops a supported exclude by omission. No such param: `exclude` is a type error,
 * so no toggle writes nothing. A mismatched type: the call fails rather than sending values the endpoint cannot
 * read.
 */
type ExcludeOption<TFits> = [TFits] extends ['absent']
  ? { exclude?: never }
  : [TFits] extends ['fits']
    ? { exclude: boolean }
    : { exclude: false | ExcludeTypeMismatch };

type ArrayExclude<Q, K extends ParamKey<Q>> = ExcludeOption<ExcludeFits<Q, K, NonNullable<Q[K]>>>;
/**
 * A single value's exclusions: `true` when `excluded<Key>` takes a list of its values, `'one'` when it takes one
 * value (`excludedProvider`), so the runtime knows which to send.
 */
type ScalarExclude<Q, K extends ParamKey<Q>> =
  ExcludeFits<Q, K, ScalarValue<Q, K>[]> extends 'fits'
    ? { exclude: boolean }
    : ExcludeFits<Q, K, ScalarValue<Q, K>> extends 'fits'
      ? { exclude: 'one' | false }
      : ExcludeOption<ExcludeFits<Q, K, ScalarValue<Q, K>[]>>;

/** Single mode shows one value, so it defaults to at most one and has no tray of sets. */
type MultiSelectMode<E extends string | number, S extends boolean> = S extends true
  ? { single: S; defaults?: readonly [] | readonly [E]; quickFilter?: never }
  : { single?: S; defaults?: readonly E[]; quickFilter?: QuickFilterSpec<E> };

/** A range writes both excluded bounds, so Exclude needs both, each holding its bound; one alone offers none. */
type RangeExclude<Q, F extends string, T extends string> = ExcludeOption<
  ExcludeFits<Q, F, NonNullable<Q[F & keyof Q]>> | ExcludeFits<Q, T, NonNullable<Q[T & keyof Q]>> extends infer R
    ? [R] extends ['fits']
      ? 'fits'
      : 'mismatch' extends R
        ? 'mismatch'
        : 'absent'
    : never
>;

/** A builder's spec: a link into the list sets the very params it sends (the URL key is the param's). */
type BuilderSpec<TParams, TControl extends FilterControl> = FilterSpec<TParams, TControl, TParams>;

/** A cleared filter sends nothing, so even a param the endpoint requires is optional here. */
type Params<Q, K extends string> = Partial<Pick<Q, Extract<K | ExcludedKey<K>, keyof Q>>>;

type SelectText = ControlText & { defaultVisibleOptions?: number };
type RangeInputText = { minLabel?: string; maxLabel?: string; minPlaceholder?: string; maxPlaceholder?: string };

/**
 * Builders bound to one endpoint's query. Each names an nsdk param and owns the control, its URL state (key =
 * the param in kebab case) and the param it writes. `useFilterUnits` resolves them. Several controls on one
 * param, or a control writing two: `unit.compose`.
 */
export const filterBuildersFor = <Q extends object>() => ({
  /**
   * Multi-select over an array param, static `options` or a `source` (`useXOptions`). `defaults` apply until the
   * user picks; clearing then lists everything. `single`: a single-select control over the same array param.
   * `quickFilter`: a tray writing this param.
   */
  multiSelect: <K extends ArrayKeyOf<Q>, S extends boolean = false>(
    key: K,
    {
      options,
      source,
      defaults,
      exclude,
      single,
      quickFilter,
      ...text
    }: SelectText & FilterOptionsInput<OptionValue<Q, K>> & MultiSelectMode<OptionValue<Q, K>, S> & ArrayExclude<Q, K>
  ) =>
    multiSelectSpec<OptionValue<Q, K>>({
      key,
      exclude: exclude === true,
      input: options ? { options } : { source },
      defaults,
      single: single === true,
      quickFilter,
      text
    }) as BuilderSpec<Params<Q, K>, S extends true ? SingleSelectControl : MultiSelectControl>,

  /** One value of a scalar param. Exclusions, when the endpoint takes `excluded<Key>` as a list, are a list. */
  singleSelect: <K extends ValueKeyOf<Q, string | number>>(
    key: K,
    {
      options,
      source,
      defaultValue,
      exclude,
      ...text
    }: SelectText & FilterOptionsInput<ScalarValue<Q, K>> & { defaultValue?: ScalarValue<Q, K> } & ScalarExclude<Q, K>
  ) =>
    singleSelectSpec<ScalarValue<Q, K>>({
      key,
      exclude: exclude === true ? 'list' : exclude === 'one' ? 'one' : false,
      input: options ? { options } : { source },
      defaultValue,
      text
    }) as BuilderSpec<Params<Q, K>, SingleSelectControl>,

  /**
   * Yes / No. `defaultValue` applies until the user picks; clearing then lists both. `onlyTrue`: one checkbox
   * that narrows to `true`, for flags whose `false` is no useful list.
   */
  boolean: <K extends ValueKeyOf<Q, boolean>, O extends boolean = false>(
    key: K,
    { defaultValue, onlyTrue, ...text }: ControlText & { defaultValue?: boolean; onlyTrue?: O }
  ) =>
    booleanSpec({ key, defaultValue, onlyTrue: onlyTrue === true, action: text }) as BuilderSpec<
      Partial<Pick<Q, K>>,
      O extends true ? CheckboxControl : BooleanControl
    >,

  /** Free text into a scalar string param. */
  text: <K extends FreeKeyOf<Q, string>>(key: K, text: ControlText & { placeholder?: string }) =>
    textSpec({ key, text }) as BuilderSpec<Partial<Pick<Q, K>>, TextControl>,

  /** One date into one param. */
  date: <K extends FreeKeyOf<Q, string>>(key: K, text: ControlText) =>
    dateSpec({ key, text }) as BuilderSpec<Partial<Pick<Q, K>>, DateControl>,

  /** Sidebar date range over `<base>Gte` / `<base>Lte`. `defaults` may be a function, for ranges relative to today. */
  dateRange: <B extends DateRangeBaseOf<Q>>(
    base: B,
    {
      defaults,
      exclude,
      ...text
    }: ControlText & { defaults?: DateRangeDefaults } & RangeExclude<Q, `${B}Gte`, `${B}Lte`>
  ) =>
    dateRangeSpec({ from: `${base}Gte`, to: `${base}Lte`, exclude: exclude === true, defaults, text }) as BuilderSpec<
      Params<Q, RangeKeys<B>>,
      DateRangeControl
    >,

  /** Sidebar date range over two params not named `<base>Gte` / `<base>Lte` (`upcomingBookingDateFrom` / `…To`). */
  dateRangeOf: <F extends FreeKeyOf<Q, string>, T extends FreeKeyOf<Q, string>>(
    keys: { from: F; to: T },
    { defaults, exclude, ...text }: ControlText & { defaults?: DateRangeDefaults } & RangeExclude<Q, F, T>
  ) =>
    dateRangeSpec({ ...keys, exclude: exclude === true, defaults, text }) as BuilderSpec<
      Params<Q, F | T>,
      DateRangeControl
    >,

  /** The toolbar date range, over any two date params (`fromDate` / `toDate`). No exclude. */
  toolbarDateRange: <F extends FreeKeyOf<Q, string>, T extends FreeKeyOf<Q, string>>(
    keys: { from: F; to: T },
    { defaults, placeholder }: { defaults?: DateRangeDefaults; placeholder?: string } = {}
  ) => dateRangeSpec({ ...keys, exclude: false, defaults, placeholder }) as BuilderSpec<Partial<Pick<Q, F | T>>, never>,

  /** Time of day over `<base>Gte` / `<base>Lte` (`HH:MM`), for bases ending in `Time`. */
  timeRange: <B extends TimeRangeBaseOf<Q>>(base: B, text: ControlText) =>
    timeRangeSpec({ base, text }) as BuilderSpec<Partial<Pick<Q, Extract<RangeKeys<B>, keyof Q>>>, TimeRangeControl>,

  /** Sidebar number range over `<base>Gte` / `<base>Lte`. `money`: entered in kroner, sent in øre. */
  numberRange: <B extends RangeBaseOf<Q, number>>(
    base: B,
    {
      exclude,
      money = false,
      ...text
    }: ControlText & RangeInputText & { money?: boolean } & RangeExclude<Q, `${B}Gte`, `${B}Lte`>
  ) =>
    numberRangeSpec({ base, exclude: exclude === true, money, text }) as BuilderSpec<
      Params<Q, RangeKeys<B>>,
      NumberRangeControl
    >
});
